import {useTranslation} from '@/translations/i18n'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Controller, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {SocialWelfareConditionsFormSchema} from '../../formSchemas'
import {
    activityAutonomyToLabel,
    applicantToLabel,
    mobilityAutonomyToLabel,
    networkSupportToLabel,
    parentalResponsibilityToLabel
} from '../../utils'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {StyledSocialWelfareConditionsFormFieldGrid} from './style'
import {Label} from '@/components/ui/label/Label'
import {RadioSteps} from '@/components/commons/radio-steps/RadioSteps'
import {ObjectKeys} from '@/utilities/helpers'
import {NetworkSupport, ProjectExtended} from '../../types'
import {useStoreSocialWelfareConditions} from '../../queries/useStoreSocialWelfareConditions'
import {useOutletContext} from 'react-router-dom'
import {useEditSocialWelfareConditions} from '../../queries/useEditSocialWelfareConditions'
import {Text} from '@/components/ui/text/Text'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'

interface SocialWelfareConditionsCardFormProps {
    onCancel: () => void
    defaultValues?: Partial<SocialWelfareConditionsFormSchema>
    isEdit?: boolean
    isEvolutionaryAge?: boolean
}

export const SocialWelfareConditionsCardForm = ({
    onCancel,
    defaultValues,
    isEdit,
    isEvolutionaryAge
}: SocialWelfareConditionsCardFormProps) => {
    const {t} = useTranslation()
    const {project} = useOutletContext<{project: ProjectExtended}>()
    const queryClient = useQueryClient()
    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_SOCIAL_WELFARE_CONDITIONS, project.id]})
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, project.id]})
        onCancel()
    }
    const {mutate: storeSocialWelfareConditions, isPending: isLoadingStoreSocialWelfareConditions} =
        useStoreSocialWelfareConditions({projectId: project.id}, {onSuccess})
    const {mutate: editSocialWelfareConditions, isPending: isLoadingEditSocialWelfareConditions} =
        useEditSocialWelfareConditions({projectId: project.id}, {onSuccess})
    const {
        control,
        handleSubmit,
        formState: {isValid, isDirty}
    } = useForm<SocialWelfareConditionsFormSchema>({
        mode: 'onBlur',
        resolver: zodResolver(SocialWelfareConditionsFormSchema),
        defaultValues: defaultValues
    })

    const onSubmit = handleSubmit(data => {
        const payload = {
            parentalResponsibility: data.parentalResponsibility || null,
            applicant: data.applicant,
            percentagePaid: data.percentagePaid,
            sharedCarePlanning: data.sharedCarePlanning,
            socialRehabilitationTreatments: data.socialRehabilitationTreatments,
            networkSupport: data.networkSupport,
            caregiver: data.caregiver,
            activityAutonomy: data.activityAutonomy,
            mobilityAutonomy: data.mobilityAutonomy
        }

        if (isEdit) {
            editSocialWelfareConditions(payload)
        } else {
            storeSocialWelfareConditions(payload)
        }
    })

    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t('projects:social_welfare_conditions')}
            headingEndSlot={
                <EditDetailsSectionButtons
                    isActive={true}
                    onCancel={onCancel}
                    onConfirm={onSubmit}
                    isDisabled={!isValid || !isDirty}
                    isLoading={isLoadingStoreSocialWelfareConditions || isLoadingEditSocialWelfareConditions}
                />
            }
        >
            <Flexbox direction="column" gap={4} fullWidth>
                <Flexbox direction="column" gap={2} fullWidth style={{maxWidth: '50%'}}>
                    {isEvolutionaryAge && (
                        <Controller
                            control={control}
                            name="parentalResponsibility"
                            render={({field, fieldState}) => (
                                <InputSelect
                                    label={t('projects:parental_responsibility')}
                                    optionsMap={parentalResponsibilityToLabel}
                                    onChange={field.onChange}
                                    value={field.value}
                                    errorMessage={fieldState.error?.message}
                                />
                            )}
                        />
                    )}

                    <Controller
                        control={control}
                        name="applicant"
                        render={({field, fieldState}) => (
                            <InputSelect
                                label={t('projects:applicant')}
                                optionsMap={applicantToLabel}
                                onChange={field.onChange}
                                value={field.value}
                                errorMessage={fieldState.error?.message}
                            />
                        )}
                    />
                </Flexbox>

                <Flexbox direction="column" gap={4}>
                    <StyledSocialWelfareConditionsFormFieldGrid>
                        <Label>{t('projects:ssn_cost_percentage')}</Label>
                        <Flexbox gap={2} align="center">
                            <Text weight="500" size="textMd">
                                100%
                            </Text>
                        </Flexbox>
                    </StyledSocialWelfareConditionsFormFieldGrid>

                    <StyledSocialWelfareConditionsFormFieldGrid>
                        <Label>{t('projects:shared_care_planning')}</Label>

                        <Controller
                            control={control}
                            name="sharedCarePlanning"
                            render={({field}) => (
                                <RadioSteps
                                    steps={[
                                        {label: t('commons:yes'), value: true},
                                        {label: t('commons:no'), value: false}
                                    ]}
                                    onChange={field.onChange}
                                    value={field.value}
                                    name={field.name}
                                    itemWidth={'80px'}
                                    size={'sm'}
                                />
                            )}
                        />
                    </StyledSocialWelfareConditionsFormFieldGrid>

                    <StyledSocialWelfareConditionsFormFieldGrid>
                        <Label>{t('projects:social_rehabilitation_treatments')}</Label>

                        <Controller
                            control={control}
                            name="socialRehabilitationTreatments"
                            render={({field}) => (
                                <RadioSteps
                                    steps={[
                                        {label: t('commons:yes'), value: true},
                                        {label: t('commons:no'), value: false}
                                    ]}
                                    onChange={field.onChange}
                                    value={field.value}
                                    name={field.name}
                                    itemWidth={'80px'}
                                    size={'sm'}
                                />
                            )}
                        />
                    </StyledSocialWelfareConditionsFormFieldGrid>

                    <StyledSocialWelfareConditionsFormFieldGrid>
                        <Label>{t('projects:formal_or_informal_networks_support')}</Label>

                        <Controller
                            control={control}
                            name="networkSupport"
                            render={({field}) => (
                                <RadioSteps<NetworkSupport>
                                    steps={ObjectKeys(networkSupportToLabel).map(item => ({
                                        label: t(networkSupportToLabel[item]),
                                        value: item
                                    }))}
                                    onChange={field.onChange}
                                    value={field.value}
                                    name={field.name}
                                    size={'sm'}
                                    itemWidth={'160px'}
                                />
                            )}
                        />
                    </StyledSocialWelfareConditionsFormFieldGrid>

                    <StyledSocialWelfareConditionsFormFieldGrid>
                        <Label>{t('projects:caregiver')}</Label>

                        <Controller
                            control={control}
                            name="caregiver"
                            render={({field}) => (
                                <RadioSteps
                                    steps={[
                                        {label: t('commons:yes'), value: true},
                                        {label: t('commons:no'), value: false}
                                    ]}
                                    onChange={field.onChange}
                                    value={field.value}
                                    name={field.name}
                                    size={'sm'}
                                    itemWidth={'80px'}
                                />
                            )}
                        />
                    </StyledSocialWelfareConditionsFormFieldGrid>
                </Flexbox>
                <Flexbox direction="column" gap={2} fullWidth style={{maxWidth: '50%'}}>
                    <Controller
                        control={control}
                        name="activityAutonomy"
                        render={({field, fieldState}) => (
                            <InputSelect
                                label={t('projects:activity_autonomy')}
                                optionsMap={activityAutonomyToLabel}
                                {...field}
                                errorMessage={fieldState.error?.message}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="mobilityAutonomy"
                        render={({field, fieldState}) => (
                            <InputSelect
                                label={t('projects:mobility_autonomy')}
                                optionsMap={mobilityAutonomyToLabel}
                                {...field}
                                errorMessage={fieldState.error?.message}
                            />
                        )}
                    />
                </Flexbox>
            </Flexbox>
        </DetailsFieldsSection>
    )
}
